import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, unref as _unref, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createBlock as _createBlock, toDisplayString as _toDisplayString, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, normalizeClass as _normalizeClass, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-785dbc90"),n=n(),_popScopeId(),n)
const _hoisted_1 = {
  key: 0,
  class: "vietnam-operations-map__unlocated"
}

import {
  computed,
  onBeforeUnmount,
  onMounted,
  ref,
  shallowRef,
  watch,
} from 'vue';
import maplibregl from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import { MapboxOverlay } from '@deck.gl/mapbox';
import { PathLayer, ScatterplotLayer } from '@deck.gl/layers';
import MapToolbar from '@/components/dashboard/map/MapToolbar.vue';
import MapEntityCalloutCard from '@/components/dashboard/map/MapEntityCalloutCard.vue';
import VietnamIslandMarker from '@/components/dashboard/map/VietnamIslandMarker.vue';
import { resolveCalloutMode } from '@/components/dashboard/map/composables/useMapCalloutMode';
import {
  getLeaderLineAlpha,
  getLeaderLineWidth,
} from '@/components/dashboard/map/composables/useMapLeaderLines';
import { useVietnamIslandMarkers } from '@/components/dashboard/map/composables/useVietnamIslandMarkers';
import { VIETNAM_ISLAND_MARKERS } from '@/components/dashboard/map/constants/vietnam-island-markers';
import {
  resolveCalloutLayout,
  screenPathToGeoPath,
  hexToRgba,
  type CalloutLayout,
} from '@/core/services/mapCalloutLayout.service';
import {
  filterLocatedMarkers,
  getMapCenterCoordinates,
  normalizeFitBounds,
} from '@/core/helpers/dashboard-map';
import {
  CAP_DON_VI,
  MAP_FIT_BOUNDS_PADDING,
  MAP_MARKER_SIZES,
  MAP_NATIONAL_FIT_PADDING,
  VIETNAM_NATIONAL_BOUNDS,
  VIETNAM_NATIONAL_MAX_ZOOM,
  MAP_MARKER_STATUS_COLORS,
} from '@/core/constants/dashboard-map.constants';
import type {
  DashboardMapFocus,
  DashboardMapItem,
  MapBounds,
} from '@/core/interfaces/Dashboard';
import {
  buildWarehouseAreaHighlight,
  getProvinceBoundsByCode,
  getVietnamBoundsFromGeoJson,
} from '@/core/services/mapGeometry.service';
import {
  addVietnamBoundaryLayers,
  ensureMapBoundarySources,
  getBoundaryGeoJson,
  getProvincesGeoJson,
  setDistrictVisibility,
  setSelectedProvinceFilter,
  updateWarehouseAreaHighlight,
} from '@/core/services/mapStyle.service';

/**
 * Label-free light basemap — Vietnam place labels come from custom layers.
 * Foreign city / POI / road labels stay off.
 */

export default /*#__PURE__*/_defineComponent({
  __name: 'VietnamOperationsMap',
  props: {
    mode: {},
    focus: {},
    markers: {},
    loading: { type: Boolean, default: false },
    selectedMarkerId: { default: null },
    unlocatedCount: { default: 0 },
    canReset: { type: Boolean, default: false },
    enableFullscreen: { type: Boolean, default: true },
    isFullscreen: { type: Boolean, default: false },
    showFinancial: { type: Boolean, default: true },
    capDonVi: { default: CAP_DON_VI.DON_VI }
  },
  emits: ["selectMarker", "resetFocus", "toggleFullscreen", "changeBounds"],
  setup(__props: any, { emit: __emit }) {

const LIGHT_MAP_STYLE: maplibregl.StyleSpecification = {
  version: 8,
  glyphs: 'https://demotiles.maplibre.org/font/{fontstack}/{range}.pbf',
  sources: {
    carto: {
      type: 'raster',
      tiles: [
        'https://basemaps.cartocdn.com/light_nolabels/{z}/{x}/{y}@2x.png',
      ],
      tileSize: 256,
      attribution: '&copy; OpenStreetMap &copy; CARTO',
    },
  },
  layers: [
    {
      id: 'carto-light',
      type: 'raster',
      source: 'carto',
      paint: {
        'raster-opacity': 0.48,
        'raster-saturation': -0.45,
        'raster-brightness-min': 0.12,
        'raster-contrast': 0.06,
      },
    },
  ],
};

const props = __props;

const emit = __emit;

const mapContainer = ref<HTMLElement | null>(null);
const calloutLayouts = shallowRef<CalloutLayout[]>([]);
const hoveredMarkerId = ref<number | null>(null);
const leaderGeoPaths = shallowRef<
  Array<{
    path: [number, number][];
    color: [number, number, number, number];
    markerId: number;
    selected: boolean;
    hovered: boolean;
  }>
>([]);

let map: maplibregl.Map | null = null;
let deckOverlay: MapboxOverlay | null = null;
let layoutFrame = 0;
let islandFrame = 0;
let boundariesReady = false;
let resizeObserver: ResizeObserver | null = null;
let lastContainerSize = { width: 0, height: 0 };

const locatedMarkers = computed(() => filterLocatedMarkers(props.markers));

const calloutMode = computed(() =>
  resolveCalloutMode(locatedMarkers.value.length)
);

const isCompanyNationalView = computed(
  () =>
    props.capDonVi === CAP_DON_VI.CONG_TY && props.focus.type === 'national'
);

const capDonViRef = computed(() => props.capDonVi);
const {
  islandScreenPositions,
  projectIslandMarkers,
  clearIslandMarkers,
} = useVietnamIslandMarkers({
  capDonVi: capDonViRef,
});

const highlightMarkers = computed(() => {
  // Warehouse hull is only a secondary highlight for company-level selection.
  // Unit (cap_don_vi=2) uses province_code polygon highlight instead.
  if (props.capDonVi === CAP_DON_VI.DON_VI) {
    return [];
  }

  if (props.selectedMarkerId == null) {
    return [];
  }

  const selected = locatedMarkers.value.find(
    (marker) => marker.id === props.selectedMarkerId
  );
  return selected ? [selected] : [];
});

function resolveProvinceHighlightCode(): string | null {
  if (props.focus.provinceCode) {
    return String(props.focus.provinceCode);
  }

  if (props.selectedMarkerId != null) {
    const selected = locatedMarkers.value.find(
      (marker) => marker.id === props.selectedMarkerId
    );
    if (selected?.provinceCode) {
      return String(selected.provinceCode);
    }
  }

  if (props.capDonVi === CAP_DON_VI.DON_VI) {
    const codes = new Set(
      locatedMarkers.value
        .map((marker) => marker.provinceCode)
        .filter((code): code is string => Boolean(code))
    );
    if (codes.size === 1) {
      return String([...codes][0]);
    }
  }

  return null;
}

function getNationalBounds(): [[number, number], [number, number]] {
  const boundary = getBoundaryGeoJson();
  const mainland = boundary
    ? getVietnamBoundsFromGeoJson(boundary)
    : VIETNAM_NATIONAL_BOUNDS;

  // Include Hoàng Sa / Trường Sa so island markers stay in frame on national fit / zoom-out.
  let [[west, south], [east, north]] = mainland;
  for (const island of VIETNAM_ISLAND_MARKERS) {
    west = Math.min(west, island.longitude);
    south = Math.min(south, island.latitude);
    east = Math.max(east, island.longitude);
    north = Math.max(north, island.latitude);
  }

  return (
    normalizeFitBounds([
      [west, south],
      [east, north],
    ]) ?? mainland
  );
}

function fitNationalVietnam(): void {
  if (!map) {
    return;
  }
  map.fitBounds(getNationalBounds(), {
    padding: MAP_NATIONAL_FIT_PADDING,
    duration: 0,
    maxZoom: VIETNAM_NATIONAL_MAX_ZOOM,
  });
}

function getMarkerBounds(): [[number, number], [number, number]] | null {
  if (!locatedMarkers.value.length) {
    return null;
  }

  let west = Number.POSITIVE_INFINITY;
  let south = Number.POSITIVE_INFINITY;
  let east = Number.NEGATIVE_INFINITY;
  let north = Number.NEGATIVE_INFINITY;

  for (const marker of locatedMarkers.value) {
    west = Math.min(west, marker.longitude);
    south = Math.min(south, marker.latitude);
    east = Math.max(east, marker.longitude);
    north = Math.max(north, marker.latitude);
  }

  return normalizeFitBounds([
    [west, south],
    [east, north],
  ]);
}

function applyFocus(): void {
  if (!map) {
    return;
  }

  if (isCompanyNationalView.value || props.focus.type === 'national') {
    fitNationalVietnam();
    return;
  }

  const provinceBounds = getProvinceBoundsByCode(
    getProvincesGeoJson(),
    props.focus.provinceCode
  );
  if (props.focus.type === 'province' && provinceBounds) {
    map.fitBounds(provinceBounds, {
      padding: MAP_FIT_BOUNDS_PADDING,
      duration: 0,
      maxZoom: 9,
    });
    return;
  }

  const focusBounds = normalizeFitBounds(props.focus.bounds ?? null);
  if (focusBounds) {
    map.fitBounds(focusBounds, {
      padding: MAP_FIT_BOUNDS_PADDING,
      duration: 0,
      maxZoom: Math.min(props.focus.zoom ?? 9, 10),
    });
    return;
  }

  const markerBounds = getMarkerBounds();
  if (markerBounds) {
    map.fitBounds(markerBounds, {
      padding: MAP_FIT_BOUNDS_PADDING,
      duration: 0,
      maxZoom: Math.min(props.focus.zoom ?? 9, 10),
    });
    return;
  }

  const center = getMapCenterCoordinates(props.focus.center);
  map.jumpTo({
    center: [center.longitude, center.latitude],
    zoom: props.focus.zoom,
  });
}

function handleContainerResize(): void {
  if (!map || !mapContainer.value) {
    return;
  }

  const rect = mapContainer.value.getBoundingClientRect();
  const width = Math.round(rect.width);
  const height = Math.round(rect.height);
  if (
    width === lastContainerSize.width &&
    height === lastContainerSize.height
  ) {
    return;
  }

  lastContainerSize = { width, height };
  map.resize();
  applyFocus();
  scheduleCalloutLayout();
  scheduleIslandOverlay();
}

function scheduleIslandOverlay(): void {
  cancelAnimationFrame(islandFrame);
  islandFrame = requestAnimationFrame(() => {
    projectIslandMarkers(map);
  });
}

function getMarkerColor(
  status: string,
  alpha = 255
): [number, number, number, number] {
  const hex =
    MAP_MARKER_STATUS_COLORS[status] ?? MAP_MARKER_STATUS_COLORS.healthy;
  return hexToRgba(hex, alpha);
}

function isMarkerSelected(markerId: number): boolean {
  return props.selectedMarkerId === markerId;
}

function isMarkerHovered(markerId: number): boolean {
  return hoveredMarkerId.value === markerId;
}

function isMarkerActive(markerId: number): boolean {
  return isMarkerSelected(markerId) || isMarkerHovered(markerId);
}

function buildDeckLayers() {
  const data = locatedMarkers.value.map((marker) => ({
    ...marker,
    position: [marker.longitude, marker.latitude] as [number, number],
    isActive: isMarkerActive(marker.id),
    isSelected: isMarkerSelected(marker.id),
  }));

  const glowLayer = new ScatterplotLayer({
    id: 'warehouse-marker-glow',
    data,
    getPosition: (d) => d.position,
    getFillColor: (d) =>
      getMarkerColor(
        d.status,
        d.isActive ? 72 : d.status === 'critical' ? 55 : 38
      ),
    getRadius: (d) => {
      if (d.isActive) {
        return MAP_MARKER_SIZES.glowRadiusSelected;
      }
      return d.status === 'critical'
        ? MAP_MARKER_SIZES.glowRadiusCritical
        : MAP_MARKER_SIZES.glowRadius;
    },
    radiusUnits: 'pixels',
    stroked: false,
    pickable: false,
  });

  const coreLayer = new ScatterplotLayer({
    id: 'warehouse-marker-core',
    data,
    getPosition: (d) => d.position,
    getFillColor: (d) => getMarkerColor(d.status, 255),
    getRadius: MAP_MARKER_SIZES.coreRadius,
    radiusUnits: 'pixels',
    getLineColor: [255, 255, 255, 255],
    lineWidthUnits: 'pixels',
    getLineWidth: MAP_MARKER_SIZES.strokeWidth,
    stroked: true,
    pickable: true,
    onHover: (info) => {
      hoveredMarkerId.value =
        info.object && (info.object as DashboardMapItem).id != null
          ? (info.object as DashboardMapItem).id
          : null;
      return true;
    },
    onClick: (info) => {
      const marker = info.object as DashboardMapItem | null;
      if (marker?.id != null) {
        onSelectMarker(marker);
      }
      return true;
    },
  });

  const leaderLayer = new PathLayer({
    id: 'warehouse-leader-lines',
    data: leaderGeoPaths.value,
    getPath: (d) => d.path,
    getColor: (d) => d.color,
    getWidth: (d) =>
      getLeaderLineWidth({
        selected: d.selected,
        hovered: d.hovered,
      }),
    widthUnits: 'pixels',
    capRounded: true,
    jointRounded: true,
    pickable: false,
  });

  // Markers above leaders so hierarchy stays marker → place → border → line → card.
  return [leaderLayer, glowLayer, coreLayer];
}

function updateDeckLayers(): void {
  if (!deckOverlay) {
    return;
  }
  deckOverlay.setProps({ layers: buildDeckLayers() });
}

function updateAreaHighlight(): void {
  if (!map || !boundariesReady) {
    return;
  }

  const provinceCode = resolveProvinceHighlightCode();
  setSelectedProvinceFilter(map, provinceCode);

  // cap_don_vi=2: province polygon + district layers; never warehouse hull.
  if (props.capDonVi === CAP_DON_VI.DON_VI) {
    updateWarehouseAreaHighlight(map, null);
    setDistrictVisibility(map, true, provinceCode);
    return;
  }

  setDistrictVisibility(map, false, null);
  const feature = buildWarehouseAreaHighlight(highlightMarkers.value);
  updateWarehouseAreaHighlight(map, feature);
}

function scheduleCalloutLayout(): void {
  if (!map || !mapContainer.value) {
    return;
  }
  cancelAnimationFrame(layoutFrame);
  layoutFrame = requestAnimationFrame(() => {
    const currentMap = map;
    if (!currentMap || !mapContainer.value) {
      return;
    }

    const rect = mapContainer.value.getBoundingClientRect();
    const viewport = { width: rect.width, height: rect.height };

    const projected = locatedMarkers.value
      .map((marker, index) => {
        const point = currentMap.project([marker.longitude, marker.latitude]);
        return {
          marker,
          x: point.x,
          y: point.y,
          index,
        };
      })
      .filter((item) => Number.isFinite(item.x) && Number.isFinite(item.y));

    calloutLayouts.value = resolveCalloutLayout(projected, viewport, {
      selectedMarkerId: props.selectedMarkerId ?? null,
      hoveredMarkerId: hoveredMarkerId.value,
      calloutMode: calloutMode.value,
    });

    leaderGeoPaths.value = calloutLayouts.value
      .filter((layout) => layout.path.length >= 2)
      .map((layout) => {
        const selected = isMarkerSelected(layout.marker.id);
        const hovered = isMarkerHovered(layout.marker.id);
        return {
          path: screenPathToGeoPath(currentMap, layout.path),
          markerId: layout.marker.id,
          selected,
          hovered,
          color: getMarkerColor(
            layout.marker.status,
            getLeaderLineAlpha({ selected, hovered })
          ),
        };
      });

    updateDeckLayers();
  });
}

function emitBounds(): void {
  if (!map) {
    return;
  }
  const b = map.getBounds();
  emit('changeBounds', {
    north: b.getNorth(),
    south: b.getSouth(),
    east: b.getEast(),
    west: b.getWest(),
  });
}

function onSelectMarker(marker: DashboardMapItem): void {
  emit('selectMarker', marker);
}

/**
 * Recenter to the default map extent for the current focus.
 * When drilled into unit/warehouse scope, also reset dashboard scope.
 */
function onResetView(): void {
  if (props.canReset) {
    emit('resetFocus');
    return;
  }
  applyFocus();
  scheduleCalloutLayout();
  scheduleIslandOverlay();
}

async function initBoundaryLayers(): Promise<void> {
  if (!map) {
    return;
  }

  await ensureMapBoundarySources(map);
  addVietnamBoundaryLayers(map);
  boundariesReady = true;
  setSelectedProvinceFilter(map, props.focus.provinceCode);
  updateAreaHighlight();
}

function initMap(): void {
  if (!mapContainer.value) {
    return;
  }

  map = new maplibregl.Map({
    container: mapContainer.value,
    style: LIGHT_MAP_STYLE,
    center: (() => {
      const center = getMapCenterCoordinates(props.focus.center);
      return [center.longitude, center.latitude];
    })(),
    zoom: Math.min(props.focus.zoom, VIETNAM_NATIONAL_MAX_ZOOM),
    minZoom: 4.2,
    maxZoom: 12,
    attributionControl: false,
    scrollZoom: false,
  });

  map.addControl(
    new maplibregl.NavigationControl({ showCompass: false }),
    'top-right'
  );

  deckOverlay = new MapboxOverlay({
    interleaved: true,
    layers: buildDeckLayers(),
  });
  map.addControl(deckOverlay as unknown as maplibregl.IControl);

  map.on('load', async () => {
    await initBoundaryLayers();
    applyFocus();
    scheduleCalloutLayout();
    scheduleIslandOverlay();
    emitBounds();
  });
  // Layout only on moveend/zoomend — never on raw move (avoids thrash + API noise).
  map.on('moveend', () => {
    scheduleCalloutLayout();
    emitBounds();
  });
  map.on('zoomend', scheduleCalloutLayout);
  map.on('resize', scheduleCalloutLayout);
  // Island overlay is static (2 markers) — safe to project on move/zoom/resize.
  map.on('move', scheduleIslandOverlay);
  map.on('zoom', scheduleIslandOverlay);
  map.on('resize', scheduleIslandOverlay);

  resizeObserver = new ResizeObserver(() => {
    handleContainerResize();
  });
  resizeObserver.observe(mapContainer.value);
}

function onViewportChange(): void {
  handleContainerResize();
}

onMounted(() => {
  initMap();
  window.addEventListener('resize', onViewportChange);
  window.visualViewport?.addEventListener('resize', onViewportChange);
});

onBeforeUnmount(() => {
  window.removeEventListener('resize', onViewportChange);
  window.visualViewport?.removeEventListener('resize', onViewportChange);
  resizeObserver?.disconnect();
  resizeObserver = null;
  cancelAnimationFrame(layoutFrame);
  cancelAnimationFrame(islandFrame);
  clearIslandMarkers();
  if (map) {
    map.remove();
    map = null;
  }
  deckOverlay = null;
});

watch(
  () => [
    locatedMarkers.value,
    props.selectedMarkerId,
    hoveredMarkerId.value,
    props.capDonVi,
    props.focus.type,
    calloutMode.value,
  ],
  () => {
    updateDeckLayers();
    updateAreaHighlight();
    scheduleCalloutLayout();
    scheduleIslandOverlay();
  },
  { deep: true }
);

watch(
  () => props.focus,
  () => {
    if (map && boundariesReady) {
      updateAreaHighlight();
    }
    applyFocus();
    scheduleCalloutLayout();
    scheduleIslandOverlay();
  },
  { deep: true }
);

watch(
  () => props.isFullscreen,
  () => {
    requestAnimationFrame(() => {
      handleContainerResize();
    });
  }
);

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    class: _normalizeClass(["vietnam-operations-map", { 'vietnam-operations-map--fullscreen': _ctx.isFullscreen }])
  }, [
    _createVNode(MapToolbar, {
      mode: _ctx.mode,
      "marker-count": locatedMarkers.value.length,
      "enable-fullscreen": _ctx.enableFullscreen,
      "is-fullscreen": _ctx.isFullscreen,
      onResetView: onResetView,
      onToggleFullscreen: _cache[0] || (_cache[0] = ($event: any) => (_ctx.$emit('toggleFullscreen')))
    }, null, 8, ["mode", "marker-count", "enable-fullscreen", "is-fullscreen"]),
    _createElementVNode("div", {
      ref_key: "mapContainer",
      ref: mapContainer,
      class: "vietnam-operations-map__stage"
    }, [
      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(islandScreenPositions), (item) => {
        return (_openBlock(), _createBlock(VietnamIslandMarker, {
          key: item.marker.id,
          marker: item.marker,
          position: item.position
        }, null, 8, ["marker", "position"]))
      }), 128)),
      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(calloutLayouts.value, (layout) => {
        return (_openBlock(), _createBlock(MapEntityCalloutCard, {
          key: layout.marker.id,
          marker: layout.marker,
          card: layout.card,
          "display-mode": layout.displayMode,
          selected: _ctx.selectedMarkerId === layout.marker.id,
          hovered: hoveredMarkerId.value === layout.marker.id,
          "show-financial": _ctx.showFinancial,
          onSelect: onSelectMarker,
          onMouseenter: ($event: any) => (hoveredMarkerId.value = layout.marker.id),
          onMouseleave: _cache[1] || (_cache[1] = ($event: any) => (hoveredMarkerId.value = null))
        }, null, 8, ["marker", "card", "display-mode", "selected", "hovered", "show-financial", "onMouseenter"]))
      }), 128)),
      (_ctx.unlocatedCount > 0)
        ? (_openBlock(), _createElementBlock("div", _hoisted_1, _toDisplayString(_ctx.unlocatedCount) + " địa điểm chưa có tọa độ hợp lệ ", 1))
        : _createCommentVNode("", true)
    ], 512)
  ], 2))
}
}

})